<template>

    <div class="unit" :class="{ compact: !showDetail }" :style="cardStyle" @click="onCardClick">
        <div class="bg-and-holder">

        <!-- Top toolbar (shown when expanded in-grid; hidden inside the modal) -->
        <div v-if="showDetail && !inModal" class="detail-bar">
          <button class="mini-btn" @click.stop="$emit('full', { unit, teamColor, enemyTeam, ownTeam })">⛶ Full card</button>
          <button class="mini-btn" @click.stop="openInBuilder" aria-label="Open this unit in the Character Builder (new tab)" title="Open a copy of this unit in the Character Builder (new tab)">✏️ Builder</button>
          <button class="mini-btn" @click.stop="expanded = false">▾ Less</button>
        </div>

        <div class="card-col profile-col">
        <div class="image-stats">
        <div class="image-holder">
        <img v-if="unit.Gender == 'Monster'"  :src="IMG_URL + unit.Class + '.gif'" :alt="unit.Class" />
        <template v-else>
        <img v-if="teamColor != 'Champion'" :src="IMG_URL + unit.Class + unit.Gender[0] + '_' + teamColor + '.gif'" :alt="unit.Class" />
        <img v-else :src="IMG_URL + unit.Class + unit.Gender[0] + '.gif'" :alt="unit.Class" />
        </template>
        <div class="class-label-row">
          <span class="class-label hasToolTip" @click="togglePin">{{ unit.Class }}
            <span v-if="unit.Gender != 'Monster'" class="toolTip"><p>{{unit.Gender}} {{unit.Class}}</p>{{toolTips.Class[unit.Class.replace(/\s+/g, '')+ ' ' + unit.Gender]}}</span>
            <span v-else class="toolTip"><p>{{unit.Class}}</p>{{toolTips.Class[unit.Class.replace(/\s+/g, '')]}}</span>
          </span>
          <span v-if="showDetail && rollAdvice" class="roll-dice hasToolTip" @click.stop="e => e.currentTarget.classList.toggle('active')" title="Best rolls for this class">🎲
            <span class="toolTip roll-tip"><p>🎲 Best {{ unit.Class }} rolls <small>(by historical win rate)</small></p>
              <template v-for="(slot, label) in rollAdvice" :key="label">
                <div v-if="slot.length" class="roll-slot"><b>{{ label }}:</b> <span v-for="(s, i) in slot" :key="i" class="roll-pick">{{ s.skill }} <small>{{ s.winRate }}%</small></span></div>
              </template>
            </span>
          </span>
        </div>

        <!-- Directional evade grid: dodge % by the side you're hit from, portrait
             in the middle, ✦ magic-evade badge in a corner. Sits under the
             portrait, left of the grade explanation. -->
        <div v-if="showDetail" class="evade-grid hasToolTip">
          <span class="eg-title">Evasion</span>
          <div class="eg-cells">
            <span class="eg-cell eg-front" :class="evClass(evade.physFront)">{{ evade.physFront }}</span>
            <span class="eg-cell eg-magic" :class="evClass(evade.magic)" title="Magic evade (any direction)">✦{{ evade.magic }}</span>
            <span class="eg-cell eg-sidel" :class="evClass(evade.physSide)">{{ evade.physSide }}</span>
            <span class="eg-cell eg-mid"><img :src="portraitSrc" :alt="unit.Class" @error="$event.target.style.visibility='hidden'"></span>
            <span class="eg-cell eg-sider" :class="evClass(evade.physSide)">{{ evade.physSide }}</span>
            <span class="eg-cell eg-back" :class="evClass(evade.physBack)">{{ evade.physBack }}</span>
          </div>
          <span v-if="evadeSources.length" class="evade-from">{{ evadeSources.join(' · ') }}</span>
          <span class="toolTip"><p>Evasion</p>Front {{ evade.physFront }}% · Side {{ evade.physSide }}% · Back {{ evade.physBack }}% physical (facing matters — flank for fewer dodges). ✦ Magic {{ evade.magic }}% ignores facing.<template v-if="evadeSources.length"><br>From: {{ evadeSources.join(' · ') }}</template></span>
        </div>
        </div>

        <div class="name-and-stats">
        

          <div :style="nameStyle(unit.Name)"><b><template v-if="unit.RaidBoss">[!] </template><PlayerHover :name="unit.Name">{{ unit.Name }}</PlayerHover></b><span class="grade-badge hasToolTip" :class="'tier-' + grade.color">{{ grade.grade }}<span class="toolTip"><p>{{ grade.color }} {{ grade.grade }} — {{ unit.Class }}</p>Type wins {{ grade.typeWR }}% ({{ grade.color }} tier); this one {{ grade.specimenLift >= 0 ? '+' : '' }}{{ grade.specimenLift }} → ~{{ grade.score }}%.<br><template v-for="p in gradeTip.up" :key="p.dim">▲ {{ p.label }} {{ signed(p.contribution) }}<br></template><template v-for="p in gradeTip.down" :key="p.dim">▼ {{ p.label }} {{ signed(p.contribution) }}<br></template><template v-if="gradeTip.more">+{{ gradeTip.more }} more — open the full card for all</template></span></span></div>

          <!-- Zodiac sign + its astrological strengths/weaknesses -->
          <div class="zodiac-line">
            <span class="hasToolTip z-sign">{{unit.Sign}}<span class="toolTip"><p>{{unit.Sign}}</p>{{toolTips.Zodiac[unit.Sign]}}</span></span>
            <template v-if="showDetail && zodiacProfile">
              <span class="z-compat z-good" title="Deals/takes more damage with these signs">▲ {{ zodiacProfile.good.join(', ') }}</span>
              <span class="z-compat z-bad" title="Deals/takes less damage with these signs">▼ {{ zodiacProfile.bad.join(', ') }}</span>
              <span v-if="unit.Gender != 'Monster'" class="z-compat z-opp" :title="'Opposite sign — Best (opposite gender) / Worst (same gender)'">⇅ {{ zodiacProfile.opposite }}</span>
            </template>
          </div>

          <div class="stats">
            <div class="stat">
              <span @click="togglePin"
                    :class="{ goodBonus: itemBonus.hp > 100, hasToolTip: itemBonus.hp > 0 }"
                    :style="wrS('StatHP', stats.hp)">HP: {{ stats.hp }}
                <span v-if="itemBonus.hp > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default HP ({{classBase.hp}})
                  <span v-if="equipped.Mainhand.hp > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.hp}})</span>
                  <span v-if="equipped.Offhand.hp > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.hp}})</span>
                  <span v-if="equipped.Head.hp > 0"> + {{equipped.Head.name}} (+{{equipped.Head.hp}})</span>
                  <span v-if="equipped.Armor.hp > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.hp}})</span>
                  <span v-if="equipped.Accessory.hp > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.hp}})</span>
                  </p>
                </span>
              </span>
              <span> - </span>
              <span @click="togglePin"
                    :class="{ goodBonus: itemBonus.mp > 40, hasToolTip: itemBonus.mp > 0 }">MP: {{ stats.mp }}
                <span v-if="itemBonus.mp > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default MP ({{classBase.mp}})
                  <span v-if="equipped.Mainhand.mp > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.mp}})</span>
                  <span v-if="equipped.Offhand.mp > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.mp}})</span>
                  <span v-if="equipped.Head.mp > 0"> + {{equipped.Head.name}} (+{{equipped.Head.mp}})</span>
                  <span v-if="equipped.Armor.mp > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.mp}})</span>
                  <span v-if="equipped.Accessory.mp > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.mp}})</span>
                  </p>
                </span>
              </span>
            </div>
            <div class="stat">
              <span @click="togglePin"
                    :class="{ goodBonus: itemBonus.speed > 2, hasToolTip: itemBonus.speed > 0 }"
                    :style="wrS('StatSpeed', stats.speed)">Speed: {{ stats.speed }}
                <span v-if="itemBonus.speed > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default Speed ({{classBase.speed}})
                  <span v-if="equipped.Mainhand.speed > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.speed}})</span>
                  <span v-if="equipped.Offhand.speed > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.speed}})</span>
                  <span v-if="equipped.Head.speed > 0"> + {{equipped.Head.name}} (+{{equipped.Head.speed}})</span>
                  <span v-if="equipped.Armor.speed > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.speed}})</span>
                  <span v-if="equipped.Accessory.speed > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.speed}})</span>
                  </p>
                </span>
              </span>
              <span> - </span>
              <span @click="togglePin"
                    :class="{ goodBonus: (itemBonus.move + moveSkillBonus.move) > 2,
                              hasToolTip: (itemBonus.move + moveSkillBonus.move) > 0 }"> Move: {{ stats.move }}
                <span v-if="(itemBonus.move + moveSkillBonus.move) > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default Move ({{classBase.move}})
                  <span v-if="equipped.Mainhand.move > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.move}})</span>
                  <span v-if="equipped.Offhand.move > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.move}})</span>
                  <span v-if="equipped.Head.move > 0"> + {{equipped.Head.name}} (+{{equipped.Head.move}})</span>
                  <span v-if="equipped.Armor.move > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.move}})</span>
                  <span v-if="equipped.Accessory.move > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.move}})</span>
                  <span v-if="moveSkillBonus.move > 0"> + {{unit.MoveSkill}} (+{{moveSkillBonus.move}})</span>
                  </p>
                </span>
              </span>
              <span> - </span>
              <span @click="togglePin"
                    :class="{ goodBonus: (itemBonus.jump + moveSkillBonus.jump) > 2,
                              hasToolTip: (itemBonus.jump + moveSkillBonus.jump) > 0 }">Jump: {{ stats.jump }}
                <span v-if="(itemBonus.jump + moveSkillBonus.jump) > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default Jump ({{classBase.jump}})
                  <span v-if="equipped.Mainhand.jump > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.jump}})</span>
                  <span v-if="equipped.Offhand.jump > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.jump}})</span>
                  <span v-if="equipped.Head.jump > 0"> + {{equipped.Head.name}} (+{{equipped.Head.jump}})</span>
                  <span v-if="equipped.Armor.jump > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.jump}})</span>
                  <span v-if="equipped.Accessory.jump > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.jump}})</span>
                  <span v-if="moveSkillBonus.jump > 0"> + {{unit.MoveSkill}} (+{{moveSkillBonus.jump}})</span>
                  </p>
                </span>
              </span>
            </div>
            <div class="stat">
              <span @click="togglePin"
                    :class="{ goodBonus: itemBonus.pa > 2, hasToolTip: itemBonus.pa > 0 }"
                    :style="wrS('StatPA', stats.pa)">PA: {{ stats.pa }}
                <span v-if="itemBonus.pa > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default PA ({{classBase.pa}})
                  <span v-if="equipped.Mainhand.pa > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.pa}})</span>
                  <span v-if="equipped.Offhand.pa > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.pa}})</span>
                  <span v-if="equipped.Head.pa > 0"> + {{equipped.Head.name}} (+{{equipped.Head.pa}})</span>
                  <span v-if="equipped.Armor.pa > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.pa}})</span>
                  <span v-if="equipped.Accessory.pa > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.pa}})</span>
                  </p>
                </span>
              </span>
              <span> - </span>
              <span @click="togglePin"
                    :class="{ goodBonus: itemBonus.ma > 2, hasToolTip: itemBonus.ma > 0 }"
                    :style="wrS('StatMA', stats.ma)">MA: {{ stats.ma }}
                <span v-if="itemBonus.ma > 0" class="toolTip">
                  <p><span v-if="unit.Gender != 'Monster'">{{unit.Gender}}</span>
                  {{unit.Class}} Default MA ({{classBase.ma}})
                  <span v-if="equipped.Mainhand.ma > 0"> + {{equipped.Mainhand.name}} (+{{equipped.Mainhand.ma}})</span>
                  <span v-if="equipped.Offhand.ma > 0"> + {{equipped.Offhand.name}} (+{{equipped.Offhand.ma}})</span>
                  <span v-if="equipped.Head.ma > 0"> + {{equipped.Head.name}} (+{{equipped.Head.ma}})</span>
                  <span v-if="equipped.Armor.ma > 0"> + {{equipped.Armor.name}} (+{{equipped.Armor.ma}})</span>
                  <span v-if="equipped.Accessory.ma > 0"> + {{equipped.Accessory.name}} (+{{equipped.Accessory.ma}})</span>
                  </p>
                </span>
              </span>
            </div>
            <div class="stat">
              <span :class="{ bold: (braveAdj > 70 || braveAdj < 45) }" :style="wrS('Brave', braveAdj)">Brave: {{braveAdj}}</span>
              -
              <span :class="{ bold: (faithAdj > 70 || faithAdj < 45) }" :style="wrS('Faith', faithAdj)">Faith: {{faithAdj}}</span>
            </div>
          </div>

          <!-- Grade explanation: how this unit's class type + this specimen score -->
          <div v-if="showDetail" class="grade-detail" :class="'tier-' + grade.color">
            <span class="gd-head">{{ grade.color }} {{ grade.grade }}</span>
            <span class="gd-type">{{ unit.Class }} type ~{{ grade.typeWR }}% · this one {{ signed(grade.specimenLift) }} → ~{{ grade.score }}%</span>
            <span class="gd-parts"><span v-for="p in gradeFactors" :key="p.dim" class="gd-part" :class="p.contribution >= 0 ? 'gd-up' : 'gd-down'">{{ p.label }} {{ signed(p.contribution) }}</span></span>
          </div>


        </div>
        </div>

          <!-- Collapsed card keeps the compact one-line evade; the expanded grid
               lives under the portrait (above). -->
          <div v-if="!showDetail" class="evasion stats">
            <div class="evade-line">
              Evade — Phys <b>F</b> {{ evade.physFront }} · <b>S</b> {{ evade.physSide }} · <b>B</b> {{ evade.physBack }} &nbsp;·&nbsp; Magic {{ evade.magic }}
            </div>
          </div>



        <!-- Compact summary (shown when collapsed): loadout at a glance -->
        <div v-if="!showDetail" class="compact-extra" @click="expanded = true">
          <span class="ce-load">
            <template v-if="unit.Gender != 'Monster'">⚔ {{ gear.Mainhand || 'Unarmed' }}<template v-if="unit.ActionSkill"> · {{ unit.ActionSkill }}</template></template>
          </span>
          <span v-if="notableSkills.length" class="ce-pills">
            <span v-for="(s, i) in notableSkills" :key="i" class="ce-skill"><span
                  class="rarity-pill" :style="{ background: s.tier.pill }">{{ s.roll }}%</span>{{ s.name }}</span>
          </span>
          <span class="expand-hint">▸ details</span>
        </div>

        <div class="unit-equipment">
          <div v-if="inModal && unit.Gender != 'Monster'" class="gear-edit-bar">
            <button class="mini-btn" @click.stop="editGear = !editGear">✎ {{ editGear ? 'Done' : 'Edit gear' }}</button>
            <span v-if="editGear && gearEdited" class="gear-reset" @click.stop="resetGear">reset</span>
          </div>
          <template v-if="!editGear">

          <div class="equipment unarmed hasToolTip" :class="{ showUnarmed: disarmed, monster: unit.Gender == 'Monster',  hasWeapon: gear.Mainhand, hasShield: itemschanged[gear.Offhand].type == 'Shield' }">
            <AbilityBreakdown ability="Unarmed" description="Pow Pow! Right in the Noggin." :breakdown="weaponBreakdowns.unarmed" />
            <img class="ability-icon" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/hand.png" alt="hand">
            <img class="gear-icon flip" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/hand.png" alt="hand">
            <span class="equipment-text" @click="togglePin">Unarmed

              <span class="damage-number">({{ unarmedDamage() }})</span>
            </span>
          </div>

          <template v-if="unit.Gender != 'Monster'">



          <div v-if="gear.Mainhand" class="equipment mainhand hasToolTip">
          <AbilityBreakdown :ability="gear.Mainhand" :description="itemschanged[gear.Mainhand].info" :breakdown="weaponBreakdowns.Mainhand" />
            <img @click="disarmed = !disarmed" class="ability-icon cursor-pointer" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/hand.png" alt="hand">
            <img class="gear-icon"  :src="IMG_URL_ITEMS + gear.Mainhand.replace(/\s+/g, '') + '.gif'"  :alt='gear.Mainhand'>
            <span class="equipment-text" :class="{ bold: (notAbles.items.includes(gear.Mainhand)),  showUnarmed: disarmed }" :style="wr('Weapon', gear.Mainhand)" :title="wrTip('Weapon', gear.Mainhand)" @click="togglePin">{{ gear.Mainhand }}

              <span class="damage-number">
                <div v-if="equipped.Mainhand.type === 'Gun' && equipped.Mainhand.element" class="aventail">{{ weaponDamage('Mainhand') }}</div>
                <template v-else-if="weaponDamage('Mainhand') !== null">({{ weaponDamage('Mainhand') }})</template>
              </span>
              <span class="disarm-toggle" @click.stop="disarmed = !disarmed">{{ disarmed ? 'armed?' : 'disarm?' }}</span>
            </span>
          </div>
          <div v-if="gear.Offhand" class="equipment offhand hasToolTip">
          <AbilityBreakdown :ability="gear.Offhand" :description="itemschanged[gear.Offhand].info" :breakdown="weaponBreakdowns.Offhand" />
            <img class="ability-icon" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/hand.png" alt="hand">
            <img class="gear-icon" :src="IMG_URL_ITEMS + gear.Offhand.replace(/\s+/g, '') + '.gif'" :alt='gear.Offhand'>
            <span class="equipment-text" :class="{ bold: (notAbles.items.includes(gear.Offhand)) }" :style="wr('Weapon', gear.Offhand)" :title="wrTip('Weapon', gear.Offhand)" @click="togglePin">{{ gear.Offhand }}

            <span class="damage-number">
              <div v-if="equipped.Offhand.type === 'Gun' && equipped.Offhand.element" class="aventail">{{ weaponDamage('Offhand') }}</div>
              <template v-else-if="weaponDamage('Offhand') !== null">({{ weaponDamage('Offhand') }})</template>
            </span>


            </span>
          </div>
          <div v-if="gear.Head" class="equipment head hasToolTip">
            <span class="toolTip"><p>{{gear.Head}}</p>{{itemschanged[gear.Head].info}}</span>
            <img class="ability-icon" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/head.png" alt="head">
            <img class="gear-icon" :src="IMG_URL_ITEMS + gear.Head.replace(/\s+/g, '') + '.gif'" :alt='gear.Head'>
            <span class="equipment-text" :class="{ bold: (notAbles.items.includes(gear.Head)) }" :style="wr('Head', gear.Head)" :title="wrTip('Head', gear.Head)" @click="togglePin">{{ gear.Head }}</span> 
          </div>
          <div v-if="gear.Armor" class="equipment armor hasToolTip">
            <span class="toolTip"><p>{{gear.Armor}}</p>{{itemschanged[gear.Armor].info}}</span>
            <img class="ability-icon" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/body.png" alt="body">
            <img class="gear-icon" :src="IMG_URL_ITEMS + gear.Armor.replace(/\s+/g, '') + '.gif'" :alt='gear.Armor'>
            <span class="equipment-text" :class="{ bold: (notAbles.items.includes(gear.Armor)) }" :style="wr('Armor', gear.Armor)" :title="wrTip('Armor', gear.Armor)" @click="togglePin">{{ gear.Armor }}</span> 
          </div>
          <div v-if="gear.Accessory" class="equipment accessory hasToolTip">
            <span class="toolTip"><p>{{gear.Accessory}}</p>{{itemschanged[gear.Accessory].info}}</span>
            <img class="ability-icon" src="https://luxor-fftbg-assets.s3.amazonaws.com/icons/accessory.png" alt="accessory">
            <img class="gear-icon" :src="IMG_URL_ITEMS + gear.Accessory.replace(/\s+/g, '') + '.gif'" :alt='gear.Accessory'>
            <span class="equipment-text" :class="{ bold: (notAbles.items.includes(gear.Accessory)) }" :style="wr('Accessory', gear.Accessory)" :title="wrTip('Accessory', gear.Accessory)" @click="togglePin">{{ gear.Accessory }}</span> 
          </div>
          <div class="status" v-if="itemschanged[gear.Mainhand].permstatus || itemschanged[gear.Offhand].permstatus || itemschanged[gear.Head].permstatus || itemschanged[gear.Armor].permstatus || itemschanged[gear.Accessory].permstatus">
            <h3>Permanent Status</h3>
            <div v-for="perm of itemschanged[gear.Mainhand].permstatus" class="hasToolTip">
              {{perm.name}} ({{gear.Mainhand}})
              <span class="toolTip"><p>{{perm.name}}</p>{{perm.info}}</span>
            </div>
            <div v-for="perm of itemschanged[gear.Offhand].permstatus" class="hasToolTip">
              {{perm.name}} ({{gear.Offhand}})
              <span class="toolTip"><p>{{perm.name}}</p>{{perm.info}}</span>
            </div>
            <div v-for="perm of itemschanged[gear.Head].permstatus" class="hasToolTip">
              {{perm.name}} ({{gear.Head}})
              <span class="toolTip"><p>{{perm.name}}</p>{{perm.info}}</span>
            </div>
            <div v-for="perm of itemschanged[gear.Armor].permstatus" class="hasToolTip">
              {{perm.name}} ({{gear.Armor}})
              <span class="toolTip"><p>{{perm.name}}</p>{{perm.info}}</span>
            </div>
            <div v-for="perm of itemschanged[gear.Accessory].permstatus" class="hasToolTip">
              {{perm.name}} ({{gear.Accessory}})
              <span class="toolTip"><p>{{perm.name}}</p>{{perm.info}}</span>
            </div>
          </div>
          <div class="status" v-if="itemschanged[gear.Mainhand].initstatus || itemschanged[gear.Offhand].initstatus || itemschanged[gear.Head].initstatus || itemschanged[gear.Armor].initstatus || itemschanged[gear.Accessory].initstatus">
            <h3>Initial Status</h3>
            <div v-for="init of itemschanged[gear.Mainhand].initstatus" class="hasToolTip">
              {{init.name}} ({{gear.Mainhand}})
              <span class="toolTip"><p>{{init.name}}</p>{{init.info}}</span>
            </div>
            <div v-for="init of itemschanged[gear.Offhand].initstatus" class="hasToolTip">
              {{init.name}} ({{gear.Offhand}})
              <span class="toolTip"><p>{{init.name}}</p>{{init.info}}</span>
            </div>
            <div v-for="init of itemschanged[gear.Head].initstatus" class="hasToolTip">
              {{init.name}} ({{gear.Head}})
              <span class="toolTip"><p>{{init.name}}</p>{{init.info}}</span>
            </div>
            <div v-for="init of itemschanged[gear.Armor].initstatus" class="hasToolTip">
              {{init.name}} ({{gear.Armor}})
              <span class="toolTip"><p>{{init.name}}</p>{{init.info}}</span>
            </div>
            <div v-for="init of itemschanged[gear.Accessory].initstatus" class="hasToolTip">
              {{init.name}} ({{gear.Accessory}})
              <span class="toolTip"><p>{{init.name}}</p>{{init.info}}</span>
            </div>

          </div>
          </template>
          </template>

          <div v-else class="gear-editor">
            <div class="ge-row" v-for="slot in ['Mainhand','Offhand','Head','Armor','Accessory']" :key="slot">
              <label>{{ slotLabel(slot) }}</label>
              <select :value="gear[slot] || ''" @change="setGear(slot, $event.target.value)">
                <option value="">{{ slot === 'Mainhand' ? 'Unarmed' : 'None' }}</option>
                <optgroup v-for="g in slotItems(slot)" :key="g.label" :label="g.label">
                  <option v-for="n in g.items" :key="n" :value="n">{{ n }}</option>
                </optgroup>
              </select>
            </div>
            <label class="ge-showall"><input type="checkbox" v-model="showAllItems"> show all items</label>
          </div>
        </div>
        </div><!-- /profile-col -->

        <div class="card-col abilities-col">
        <div v-if="hasElementAffinity" class="status element-affinity">
          <h3>Elemental</h3>
          <div class="ea-rows">
            <span v-for="r in affinityRows" :key="r.kind + r.el" class="ea-tag hasToolTip" :class="r.good ? 'ea-good' : 'ea-bad'">
              <span class="ea-bullet">{{ r.good ? '+' : '–' }}</span>{{ r.label }} {{ r.el }}<template v-if="r.spells.length"> ({{ r.spells.length }})</template>
              <span class="toolTip"><p>{{ r.label }} {{ r.el }}</p>{{ r.tip }}<template v-if="r.spells.length"><br>Boosts: {{ r.spells.join(', ') }}</template><template v-if="r.sources"><br>From: {{ r.sources }}</template></span>
            </span>
          </div>
        </div>

        <div v-if="charStats[unit.Class.replace(/\s+/g, '')+[unit.Gender]].innates || unit.Class == 'Lancer'" class="unit-innates">
            <div v-for="innate of charStats[unit.Class.replace(/\s+/g, '')+[unit.Gender]].innates" class="innate hasToolTip">
                <span class="toolTip"><p>{{innate.name}}</p>{{innate.info}}</span>
                <img class="ability-icon" :src="IMG_URL_ITEMS + innate.type + '.gif'" :alt="innate.type">
                <span class="innate-text" :class="{ bold: (notAbles.abilities.includes(innate.name)) }"  @click="togglePin">{{ innate.name }}</span> 
            </div>
            <template v-if="unit.Gender != 'Monster'" >
                <div v-if="unit.Class != 'Mime'" class="innate hasToolTip" @click="togglePin">
                    <span class="toolTip"><p>{{unit.ActionSkill}}</p>{{toolTips.UserSkill[unit.ActionSkill.replace(/\s+/g, '')]}}</span>
                    <img class="ability-icon" :src="IMG_URL_ITEMS + 'active.gif'" alt="active">
                    <span class="innate-text" :class="{ bold: (notAbles.abilities.includes(unit.ActionSkill)) }" :style="wr('ActionSkill', unit.ActionSkill)" :title="wrTip('ActionSkill', unit.ActionSkill)" @click="togglePin">{{unit.ActionSkill}}</span>
                </div>

                <div :class="{ bold: (notAbles.abilities.includes(unit.ReactionSkill)) }" class="innate hasToolTip" @click="togglePin" >
                    <span class="toolTip"><p>{{unit.ReactionSkill}}</p>{{toolTips.Ability[unit.ReactionSkill]}}</span>
                    <img class="ability-icon" :src="IMG_URL_ITEMS + 'react.gif'" alt="reaction">
                    <span class="innate-text" :class="{ bold: (notAbles.abilities.includes(unit.ReactionSkill)) }" :style="wr('ReactionSkill', unit.ReactionSkill)" :title="wrTip('ReactionSkill', unit.ReactionSkill)" @click="togglePin">{{unit.ReactionSkill}}</span>
                </div>
                
                <div :class="{ bold: (notAbles.abilities.includes(unit.SupportSkill)) }" class="innate hasToolTip" @click="togglePin" >
                    <span class="toolTip"><p>{{unit.SupportSkill}}</p>{{toolTips.Ability[unit.SupportSkill]}}</span>
                    <img class="ability-icon" :src="IMG_URL_ITEMS + 'support.gif'" alt="support">
                    <span class="innate-text" :class="{ bold: (notAbles.abilities.includes(unit.SupportSkill)) }" :style="wr('SupportSkill', unit.SupportSkill)" :title="wrTip('SupportSkill', unit.SupportSkill)" @click="togglePin">{{unit.SupportSkill}}</span>
                </div>

                <div :class="{ bold: (notAbles.abilities.includes(unit.MoveSkill)) }" class="innate hasToolTip" @click="togglePin">
                    <span class="toolTip"><p>{{unit.MoveSkill}}</p>{{toolTips.Ability[unit.MoveSkill]}}</span>
                    <img class="ability-icon" :src="IMG_URL_ITEMS + 'move.gif'" alt="movement">
                    <span class="innate-text" :class="{ bold: (notAbles.abilities.includes(unit.MoveSkill)) }" :style="wr('MoveSkill', unit.MoveSkill)" :title="wrTip('MoveSkill', unit.MoveSkill)" @click="togglePin">{{unit.MoveSkill}}</span>
                </div>
            </template>
        </div>


          
          <div class="unit-skills damage-tuning">
            <div class="tuning-head" @click="showTuning = !showTuning">⚙ Damage tuning {{ showTuning ? '▾' : '▸' }}</div>
            <div v-if="showTuning" class="tuning-body">
              <label class="tuning-row"><span class="tr-label">Brave <b>{{ braveAdj }}</b></span>
                <input type="range" min="0" max="100" v-model.number="braveAdj" class="slider"></label>
              <label class="tuning-row"><span class="tr-label">Faith <b>{{ faithAdj }}</b></span>
                <input type="range" min="0" max="100" v-model.number="faithAdj" class="slider"></label>
              <label class="tuning-row"><span class="tr-label">Tgt Faith <b>{{ targetFaith }}</b></span>
                <input type="range" min="0" max="100" v-model.number="targetFaith" class="slider"></label>
              <div class="tuning-row tuning-zodiac"><span class="tr-label">Target sign</span>
                <span class="z-toggles">
                  <button v-for="z in ['Worst','Bad','Neutral','Good','Best']" :key="z" type="button"
                          class="z-toggle" :class="['z-' + z.toLowerCase(), { on: zodiacAdj === z }]"
                          :title="zodiacToggleTip(z)" @click="zodiacAdj = z">{{ z }}</button>
                </span>
              </div>
              <label class="tuning-check"><input type="checkbox" v-model="disarmed"> treat weapon as removed</label>
            </div>
          </div>

          
        <div class="unit-skills" v-if="unit.Gender != 'Monster'">
          <div v-for="ability of unit.ClassSkills" class="hasToolTip" @click="togglePin"><span
                v-if="classSkillRarity[ability]" class="rarity-pill"
                :style="{ background: classSkillRarity[ability].tier.pill }"
                :title="classSkillRarity[ability].tier.label + ' — ' + classSkillRarity[ability].roll + '% roll'">{{ classSkillRarity[ability].roll }}%</span><span :style="wr('Ability', ability)" :title="wrTip('Ability', ability)">{{ability}}</span>
              <AbilityBreakdown :ability="ability" :description="toolTips.Ability[ability]" :breakdown="abilityBreakdowns[ability]" />
                <span v-if="abilityValues[ability]">&nbsp;({{abilityValues[ability]}})</span>
          </div>
        </div>


        <div class="unit-skills" v-if="unit.Gender != 'Monster' && unit.Class != 'Mime' ">
          <div v-for="ability of unit.ExtraSkills" class="hasToolTip" @click="togglePin"><span
                v-if="extraSkillRarity[ability]" class="rarity-pill"
                :style="{ background: extraSkillRarity[ability].tier.pill }"
                :title="extraSkillRarity[ability].tier.label + ' — ' + extraSkillRarity[ability].roll + '% roll'">{{ extraSkillRarity[ability].roll }}%</span><span :style="wr('Ability', ability)" :title="wrTip('Ability', ability)">{{ability}}</span>
              <AbilityBreakdown :ability="ability" :description="toolTips.Ability[ability]" :breakdown="abilityBreakdowns[ability]" />
                <span v-if="abilityValues[ability]">&nbsp;({{abilityValues[ability]}})</span>
          </div>
        </div>


           
        <div class="unit-skills" v-if="toolTips.MonsterSkills[unit.Class]">
          <div v-for="ability of toolTips.MonsterSkills[unit.Class]" :class="{ bold: (notAbles.abilities.includes(ability)) }" class="hasToolTip" @click="togglePin">{{ability}}
              <AbilityBreakdown :ability="ability" :description="toolTips.Ability[ability.replace(/[^a-zA-Z1-9 ]/g, '')]" :breakdown="abilityBreakdowns[ability]" />
                <span v-if="abilityValues[ability]">&nbsp;({{abilityValues[ability]}})</span>
          </div>
        </div>
        </div><!-- /abilities-col -->
    </div>
    </div>



</template>
<script>
import { weaponDamage as calcWeaponDamage, unarmedDamage as calcUnarmedDamage, weaponAttackModel, unarmedAttackModel } from '../lib/damage.js';
import { abilityDisplay, legacyAbilityFormula, abilityVsTarget, computeAbilityDamage } from '../lib/abilityFormula.js';
import { unitElementProfile, unitElementSources, parseItemElements } from '../lib/elements.js';
import { skillRarity } from '../lib/skillRarity.js';
import skillRarityData from '../data/skillRarity.json';
import { teamBright, teamDark } from '../lib/teamColors.js';
import { hitChance } from '../lib/evasion.js';
import classStats from '../data/classStats.json';
import itemsBackup from '../data/items.backup.json';
import { rosterStats } from '../lib/preload.js';
import PlayerHover from './PlayerHover.vue';
import AbilityBreakdown from './AbilityBreakdown.vue';
import { unitGrade, topParts } from '../lib/unitGrade.js';
import { COMPAT_TABLE, zodiacCompat } from '../lib/zodiac.js';
import { classAdvice } from '../lib/classAdviceLive.js';
import { wrColor, wrTitle, wrStatColor, wrStatTitle, wrMonsterBFColor } from '../lib/winRateColor.js';
import { archetypeOf, sBucket, bfBucket } from '../lib/unitGrade.js';
import { encodeBuild } from '../lib/streamFormat.js';

// Item pool for the full-card gear editor (names grouped by equip type).
const ITEMS = Array.isArray(itemsBackup) ? itemsBackup : Object.values(itemsBackup).find(Array.isArray);
const ITEM_BY_TYPE = {};
for (const it of ITEMS) (ITEM_BY_TYPE[it.type] ||= []).push(it.name);
for (const t of Object.keys(ITEM_BY_TYPE)) ITEM_BY_TYPE[t].sort();
const ALL_WEAPON_TYPES = [...new Set(ITEMS.filter((i) => i.slot === 'hand' && i.type !== 'Shield').map((i) => i.type))];
const EQUIP_SKILL_TYPE = {
  'Equip Knife': 'Knife', 'Equip Sword': 'Sword', 'Equip Axe': 'Axe',
  'Equip Polearm': 'Polearm', 'Equip Bow': 'Bow', 'Equip Gun': 'Gun',
};

// Pretty-print the base damage/heal/hit formula straight from an ability's API
// `info` string (e.g. "Effect: Damage (PA * (WP + 2))" → "PA × (WP + 2)";
// "Hit: Faith(MA + 175)%" → "Faith(MA + 175)%"). Textual only — the per-target
// numbers still run the real engine. Mirrors parseAbilityInfo's clause split so
// it reads the same clause the model does. Returns null if nothing recognizable.
function formulaText(info) {
  if (!info) return null;
  // Drop a single balanced wrapping paren pair ("(PA * WP)" → "PA * WP") but
  // keep inner grouping ("(PA * (WP + 2))" → "PA * (WP + 2)").
  const stripOuter = (e) => {
    if (e[0] !== '(' || e[e.length - 1] !== ')') return e;
    let d = 0;
    for (let i = 0; i < e.length; i++) {
      if (e[i] === '(') d++;
      else if (e[i] === ')' && --d === 0) return i === e.length - 1 ? e.slice(1, -1) : e;
    }
    return e;
  };
  const pretty = (e) => stripOuter(e.trim()).replace(/\*/g, '×').replace(/\s+/g, ' ').trim();
  const effect = (info.match(/Effect:\s*(.+)$/) || [])[1] || '';
  // Two-branch equipment conditional (Unyielding Cut): show the common (equipped)
  // branch — parseAbilityInfo also evaluates that branch by default. Handled before
  // the ';'-split so the "If …," strip below doesn't fold both branches together.
  const cond = effect.match(/^If target is equipped[^,]*,\s*Damage\s+(.+?)\.\s+If none is equipped,/);
  if (cond) return pretty(cond[1].trim());
  for (const clause of effect.split(';')) {
    const cm = clause.trim().replace(/^If [^,]+,\s*/, '').match(/^(Damage|Heal|HealMP|DamageMP|AbsorbHP|AbsorbMP)\s+(.+?)\.?\s*$/);
    if (cm && !/^\(?\d+\)?%$/.test(cm[2].trim())) return pretty(cm[2].trim());
  }
  const hit = (info.match(/Hit:\s*([^.]+?)%/) || [])[1];
  if (hit) return pretty(hit.split(';').pop().trim()) + '%';
  return null;
}

// The by-type basic-attack formula for a weapon (mirrors damage.js weaponDamage);
// substituteFormula plugs in the unit's stats for the numeric line. Brackets are
// display sugar for the floor()s. Doublehand appends "× 2".
function weaponFormulaText(item, unit) {
  if (!item || !item.type) return null;
  const dbl = unit.SupportSkill === 'Doublehand' ? ' × 2' : '';
  switch (item.type) {
    case 'Rod': case 'Crossbow': case 'Spear': case 'Sword': return `PA × WP${dbl}`;
    case 'Pole': case 'Staff': return `MA × WP${dbl}`;
    case 'Knight Sword': case 'Katana': return `[PA × Brave / 100] × WP${dbl}`;
    case 'Harp': case 'Book': case 'Fabric': return `[(PA + MA) / 2] × WP${dbl}`;
    case 'Ninja Blade': case 'Knife': case 'Bow': return `[(PA + Speed) / 2] × WP${dbl}`;
    case 'Axe': case 'Bag': case 'Flail': return `(1…PA) × WP${dbl}`;
    case 'Gun': return item.element ? 'Faith(WP × 16)' : 'WP × WP';
    default: return null;
  }
}
function unarmedFormulaText(unit) {
  const ma = (unit.SupportSkill === 'Martial Arts' || unit.Class === 'Monk') ? '3/2 × ' : '';
  return `${ma}PA × [PA × Brave / 100]`;
}

// A damage ability's status rider, if any ("…; Chance to add Death Sentence." →
// "Death Sentence"; "…Confusion, Death Sentence (Random)" → "Confusion, Death
// Sentence"). The "(Random)" tail and "Chance to knockback" are dropped. Null
// for pure-status spells (they use "Add X" as the effect itself, shown as hit%).
function riderStatuses(info) {
  if (!info) return null;
  const m = info.match(/Chance to add ([^.;(]+)/);
  return m ? m[1].trim() : null;
}

// Second "actual values" line for a formula: substitute the unit's own stats
// into the symbolic expression, expanding the Faith(X) macro into its % factors.
//   "MA × ((PA + 2) / 2)"  (MA 12, PA 10)          → "12 × ((10 + 2) / 2)"
//   "Faith(MA × 16)"       (Faith 70, tgt 60, MA 12) → "70% × 60% × 12 × 16"
function substituteFormula(formula, ctx) {
  if (!formula) return null;
  const sub = { MaxHP: ctx.maxHp, TargetFaith: ctx.targetFaith, Speed: ctx.speed,
    Brave: ctx.brave, Faith: ctx.faith, MA: ctx.ma, PA: ctx.pa, WP: ctx.wp };
  const expanded = formula.replace(/Faith\(([^)]*)\)/g,
    (_, inner) => `${ctx.faith}% × ${ctx.targetFaith}% × ${inner}`);
  return expanded.replace(/\b(MaxHP|TargetFaith|Speed|Brave|Faith|MA|PA|WP)\b/g, (t) => sub[t]);
}

// Click-to-pin dismissal: a pinned card tooltip (.hasToolTip.visible) stays open
// until you click OFF it or press Escape. Installed once for the whole app.
function closeAllPins() {
  document.querySelectorAll('.hasToolTip.visible').forEach((el) => el.classList.remove('visible'));
}
let _pinCloserInstalled = false;
function installPinCloser() {
  if (_pinCloserInstalled || typeof document === 'undefined') return;
  _pinCloserInstalled = true;
  // A click that doesn't land on a .hasToolTip row dismisses every pinned tooltip.
  // (Clicks inside a pinned breakdown are @click.stop'd, so they never reach here.)
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest('.hasToolTip')) closeAllPins();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeAllPins(); });
}

export default {
  components: { PlayerHover, AbilityBreakdown },
  emits: ['full'],
  props: {
    unit: {},
    itemschanged: {},
    charStats: {},
    notAbles: {},
    toolTips: {},
    teamColor : "",
    classRank: {},
    // Map of ability name -> API `info` string. Drives the parser-first
    // ability-damage formulas (lib/abilityFormula.js); falls back to the
    // legacy switch when an ability isn't recognized.
    abilitiesInfo: { type: Object, default: () => ({}) },
    // Map<lowercase-username, color> from the settings modal's custom
    // highlighted users — when this card's unit matches, the unit-name
    // line picks up that color.
    highlightColors: { type: Object, default: () => new Map() },
    // When rendered inside the full-card modal: always show full detail and
    // hide the compact/Full-card toolbar (the modal IS the full view).
    inModal: { type: Boolean, default: false },
    // Start expanded (detailed) rather than compact — fed from the user's
    // "expand cards by default" setting. Per-card toggle still works, and
    // flipping the setting updates already-mounted cards (watch below).
    defaultExpanded: { type: Boolean, default: false },
    showWinRates: { type: Boolean, default: false },
    // Optional opponent / own rosters (arrays of unit objects) — when present,
    // hovering a damaging spell shows per-enemy damage (zodiac-aware) and a
    // heal shows per-ally healing. Fed on the main page from the bracket matchup.
    enemyTeam: { type: Array, default: () => [] },
    ownTeam: { type: Array, default: () => [] },
  },

  computed: {
    // Empirical quality grade from rosterStats (lib/unitGrade.js) — correlational.
    grade() { return unitGrade(this.unit, rosterStats, { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles }); },
    gradeTip() { return topParts(this.grade.parts, 2); }, // compact hover: 2 biggest +/-
    // Intrinsic astrological profile of this unit's sign — the signs it's
    // Good/Bad against, plus its opposite (Best/Worst). From the FFT compat
    // table; null for Serpentarius / unknown signs (Neutral with everything).
    zodiacProfile() {
      const e = COMPAT_TABLE[this.unit.Sign];
      if (!e) return null;
      return { good: e.good, bad: e.bad, opposite: e.opposite };
    },
    // Empirical "what to roll for this class" picks (win-rate-ranked Support /
    // Reaction / Move), for the 🎲 dice helper under the portrait. Humans only.
    rollAdvice() {
      if (this.unit.Gender === 'Monster' || !this.unit.Class) return null;
      const a = classAdvice(rosterStats, this.unit.Class, { gender: this.unit.Gender });
      return (a.Support.length || a.Reaction.length || a.Move.length) ? a : null;
    },
    // The unit's portrait gif (same logic as the image-holder), reused as the
    // centerpiece of the evasion direction grid.
    portraitSrc() {
      const u = this.unit;
      if (u.Gender === 'Monster') return this.IMG_URL + u.Class + '.gif';
      if (this.teamColor !== 'Champion') return this.IMG_URL + u.Class + u.Gender[0] + '_' + this.teamColor + '.gif';
      return this.IMG_URL + u.Class + u.Gender[0] + '.gif';
    },
    gradeFactors() { // always-open list: more verbose when expanded, everything in the modal
      const parts = this.grade.parts || [];
      if (this.inModal) return [...parts].filter((p) => p.contribution).sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));
      const t = topParts(parts, 3); return [...t.up, ...t.down];
    },
    // Full detail shows when the user expanded the card, or always in the modal.
    showDetail() { return this.inModal || this.expanded; },
    // Hover-card aesthetic: dark team-colored fill + bright team border.
    cardStyle() { return { background: teamDark(this.teamColor), borderColor: teamBright(this.teamColor) }; },
    classKey() {
      return (this.unit.Class || '').replace(/\s+/g, '') + (this.unit.Gender || '');
    },
    classBase() { return this.charStats[this.classKey]; },
    // Class Stats record (preferred-arms / legal equip types) for the gear editor.
    classStatsRec() {
      const c = classStats[this.unit.Class];
      const g = (this.unit.Gender || 'Male')[0];
      return (c && (c[g] || c.M || c.F)) || {};
    },
    // Effective gear: the rolled slots, overridden by the full-card editor.
    // Everything (stats, damage, evasion, elements, status) derives from
    // `equipped`, which reads this — so edits recompute the whole card.
    gear() {
      const u = this.unit || {};
      const o = this.gearOverrides;
      const slot = (k) => (k in o ? o[k] : u[k]);
      return {
        Mainhand: slot('Mainhand'), Offhand: slot('Offhand'), Head: slot('Head'),
        Armor: slot('Armor'), Accessory: slot('Accessory'),
      };
    },
    gearEdited() { return Object.keys(this.gearOverrides).length > 0; },
    equipped() {
      // "treat weapon as removed" (disarmed) zeroes the mainhand for ALL derived
      // math — stats, damage, evasion, elements, ability formulas — while the
      // weapon name still shows (struck through) via `gear`. itemschanged[''] is
      // the safeDict zero default, the same path a genuinely weaponless unit hits.
      const mainhand = this.disarmed ? '' : this.gear.Mainhand;
      return {
        Mainhand:  this.itemschanged[mainhand],
        Offhand:   this.itemschanged[this.gear.Offhand],
        Head:      this.itemschanged[this.gear.Head],
        Armor:     this.itemschanged[this.gear.Armor],
        Accessory: this.itemschanged[this.gear.Accessory],
      };
    },
    itemBonus() {
      const e = this.equipped;
      const sum = (k) => e.Mainhand[k] + e.Offhand[k] + e.Head[k] + e.Armor[k] + e.Accessory[k];
      return {
        hp: sum('hp'), mp: sum('mp'),
        pa: sum('pa'), ma: sum('ma'),
        speed: sum('speed'),
        move: sum('move'), jump: sum('jump'),
      };
    },
    itemBonusPAUnarmed() {
      const e = this.equipped;
      return e.Head.pa + e.Armor.pa + e.Accessory.pa;
    },
    moveSkillBonus() {
      return this.notAbles.movementSkills[this.unit.MoveSkill];
    },
    // name -> rarity ({tier, roll, ...}) for own-class (primary) skills and
    // the secondary set. null entries (common / unlisted) are skipped.
    classSkillRarity() {
      const m = {};
      for (const a of this.unit.ClassSkills || []) m[a] = skillRarity(a, false, skillRarityData);
      return m;
    },
    extraSkillRarity() {
      const m = {};
      for (const a of this.unit.ExtraSkills || []) m[a] = skillRarity(a, true, skillRarityData);
      return m;
    },
    // The unit's non-common action skills, rarest first — drives the compact
    // summary's rarity pills.
    notableSkills() {
      const out = [];
      for (const a of this.unit.ClassSkills || []) { const r = this.classSkillRarity[a]; if (r) out.push({ name: a, ...r }); }
      for (const a of this.unit.ExtraSkills || []) { const r = this.extraSkillRarity[a]; if (r) out.push({ name: a, ...r }); }
      return out.sort((x, y) => x.roll - y.roll);
    },
    // Evade % (chance to dodge) by direction, via lib/evasion.js.
    evade() {
      const t = { equipped: this.equipped, cEv: (this.classBase && this.classBase.cEvPercent) || 0, reaction: this.unit.ReactionSkill };
      const ev = (opts) => Math.round(100 - hitChance(t, opts));
      return {
        physFront: ev({ type: 'physical', direction: 'front' }),
        physSide: ev({ type: 'physical', direction: 'side' }),
        physBack: ev({ type: 'physical', direction: 'back' }),
        magic: ev({ type: 'magical' }),
      };
    },
    // Where the evasion comes from (full-card "from:" line).
    evadeSources() {
      const e = this.equipped, out = [];
      const c = (this.classBase && this.classBase.cEvPercent) || 0;
      if (c) out.push((this.unit.Class || 'Class') + ' ' + c + '%');
      if (e.Offhand.type === 'Shield' && (e.Offhand.pevade || e.Offhand.mevade)) out.push((e.Offhand.name || 'Shield') + ' ' + Math.max(e.Offhand.pevade, e.Offhand.mevade) + '%');
      if (e.Accessory.pevade || e.Accessory.mevade) out.push((e.Accessory.name || 'Accessory') + ' ' + Math.max(e.Accessory.pevade, e.Accessory.mevade) + '%');
      // Parry dodges with EACH weapon (hitChance counts both), so a dual-wielder lists both.
      const parry = [e.Mainhand.wevade, e.Offhand.wevade].filter(Boolean);
      if (this.unit.ReactionSkill === 'Parry' && parry.length) out.push('Parry ' + parry.map((w) => w + '%').join(' + '));
      return out;
    },
    // ability name -> element (from the API info string), for the unit's skills.
    abilityElements() {
      const m = {};
      for (const a of [...(this.unit.ClassSkills || []), ...(this.unit.ExtraSkills || [])]) {
        const info = this.abilitiesInfo[a];
        const match = info && info.match(/Element:\s*([A-Za-z]+)/);
        if (match) m[a] = match[1];
      }
      return m;
    },
    // Each Strengthen element + the unit's own spells it boosts.
    stats() {
      const c = this.classBase, b = this.itemBonus, m = this.moveSkillBonus;
      return {
        hp:    c.hp    + b.hp,
        mp:    c.mp    + b.mp,
        pa:    c.pa    + b.pa,
        ma:    c.ma    + b.ma,
        speed: c.speed + b.speed,
        move:  c.move  + b.move  + m.move,
        jump:  c.jump  + b.jump  + m.jump,
      };
    },
    paUnarmed() {
      return this.classBase.pa + this.itemBonusPAUnarmed;
    },
    // A monster's INNATE elemental affinities — monsters have no gear, so this
    // is their only source. Read from the LIVE tips (toolTips.Class), whose
    // "Effect: Half X; Weak Y, Z" text is the same format as item info, so
    // parseItemElements handles it. (An old committed jobStats snapshot had this
    // data too but was stale — it claimed Tiamat weak Dark, Hydra half Fire.)
    // null for humans (they get affinities from gear only).
    monsterElements() {
      if (this.unit.Gender !== 'Monster') return null;
      const key = (this.unit.Class || '').replace(/\s+/g, '');
      return parseItemElements(this.toolTips.Class && this.toolTips.Class[key]);
    },
    // Aggregated elemental affinities: the unit's gear (lib/elements.js) plus, for
    // a monster, its innate affinities merged in.
    elementProfile() {
      const p = unitElementProfile(this.equipped);
      const mel = this.monsterElements;
      if (mel) for (const k of Object.keys(p)) for (const e of mel[k]) p[k].add(e);
      return p;
    },
    hasElementAffinity() {
      const p = this.elementProfile;
      return p.absorb.size || p.half.size || p.weak.size || p.strengthen.size || p.cancel.size;
    },
    elementSources() {
      const s = unitElementSources(this.equipped);
      const mel = this.monsterElements;
      if (mel) for (const k of Object.keys(s)) for (const e of mel[k]) (s[k][e] || (s[k][e] = [])).push('innate');
      return s;
    },
    // One readable row per elemental affinity: { kind, el, label, good, sources,
    // spells, tip }. Order: strengthen, absorb, cancel, half (good) → weak (bad).
    affinityRows() {
      const p = this.elementProfile, src = this.elementSources, rows = [];
      const from = (kind, el) => (src[kind] && src[kind][el]) ? src[kind][el].join(', ') : '';
      const KINDS = [
        ['strengthen', 'Strengthens', true,  (el) => `Your ${el} attacks deal ×1.25 damage.`],
        ['absorb',     'Absorbs',     true,  (el) => `${el} damage heals this unit instead.`],
        ['cancel',     'Cancels',     true,  (el) => `${el} damage is nullified (0).`],
        ['half',       'Halves',      true,  (el) => `${el} damage is halved.`],
        ['weak',       'Weak to',     false, (el) => `${el} damage is doubled against this unit.`],
      ];
      for (const [kind, label, good, tipFn] of KINDS) {
        for (const el of [...p[kind]]) {
          const sources = from(kind, el);
          const spells = kind === 'strengthen'
            ? Object.entries(this.abilityElements).filter(([, e]) => e === el).map(([a]) => a)
            : [];
          rows.push({ kind, el, label, good, sources, spells, tip: tipFn(el) });
        }
      }
      return rows;
    },

    // Ability names this card renders (both human skill lists + the monster
    // kit), deduped — the key set for the two memoized maps below.
    abilityNames() {
      const u = this.unit || {};
      const monster = (this.toolTips.MonsterSkills && this.toolTips.MonsterSkills[u.Class]) || [];
      return [...new Set([...(u.ClassSkills || []), ...(u.ExtraSkills || []), ...monster])];
    },
    // Memoized inline "(NNN)" value per ability. abilityFormula() was called
    // twice per row in the template and re-ran on every reactive render; funnel
    // it through a computed so it's one eval per ability, cached until a real
    // dep (stats / tuning sliders / gear) changes.
    abilityValues() {
      const m = {};
      for (const a of this.abilityNames) m[a] = this.abilityFormula(a);
      return m;
    },
    // Memoized per-target spell breakdown (the hover popup). targetBreakdown()
    // was called FOUR times per row (v-if, class, label, v-for) — this collapses
    // it to one eval per ability and caches across the slider-drag re-renders.
    abilityBreakdowns() {
      const m = {};
      for (const a of this.abilityNames) m[a] = this.targetBreakdown(a);
      return m;
    },
    // Basic-attack breakdown per weapon slot (the same both-teams treatment as
    // an ability, run over the weapon's attack model). Cached like the ability
    // maps so slider drags don't re-run it.
    weaponBreakdowns() {
      return {
        Mainhand: this.buildWeaponBreakdown('Mainhand'),
        Offhand: this.buildWeaponBreakdown('Offhand'),
        unarmed: this.buildWeaponBreakdown('unarmed'),
      };
    },
  },

  mounted() { installPinCloser(); },
  methods: {
    // Pin/unpin a card tooltip on click. Single-pin: opening one closes any other
    // that's open, so tapping around the card swaps the popup instead of stacking
    // them. Clicking off any tooltip (or Escape) dismisses it — see installPinCloser.
    togglePin(e) {
      const row = e.currentTarget.closest('.hasToolTip');
      if (!row) return;
      const wasOpen = row.classList.contains('visible');
      closeAllPins();
      if (!wasOpen) row.classList.add('visible');
    },
    // Copy this exact unit into the Character Builder (new tab, so the bracket stays open).
    openInBuilder() {
      if (!this.unit || !this.unit.Name) return;  // BuilderView ignores a nameless payload — don't open a blank tab
      const url = window.location.origin + window.location.pathname + '?build&u=' + encodeBuild(this.unit, this.unit.Name);
      window.open(url, '_blank', 'noopener');
    },
    wr(dim, value) { return this.showWinRates ? { color: wrColor(dim, value, this.unit.Class, this.unit.Gender) } : {}; },
    wrTip(dim, value) { return this.showWinRates ? wrTitle(dim, value, this.unit.Class, this.unit.Gender) : ''; },
    wrS(dim, raw) { if (!this.showWinRates) return {}; const isMonster = this.unit.Gender === 'Monster'; if (isMonster && dim.startsWith('Stat')) return {}; if (isMonster && (dim === 'Brave' || dim === 'Faith')) return { color: wrMonsterBFColor(this.unit.Class, dim, raw, this.grade.typeWR) }; const a = archetypeOf(this.classBase, isMonster); const b = dim === 'Brave' || dim === 'Faith' ? bfBucket(raw) : (sBucket[dim.replace('Stat', '')] || (() => String(raw)))(raw); const cg = isMonster ? null : (this.unit.Class && this.unit.Gender ? this.unit.Class + ' ' + this.unit.Gender : null); return { color: wrStatColor(dim, a, b, this.grade.typeWR, cg) }; },

    // Click anywhere on a compact card to expand it. No-op once detailed
    // (so inner controls/tooltips keep working) or inside the modal.
    onCardClick() { if (!this.showDetail) this.expanded = true; },
    signed(d) { return (d >= 0 ? '+' : '−') + Math.abs(d).toFixed(1); },
    // Color tier for an evade % cell in the direction grid.
    evClass(pct) { return pct >= 40 ? 'ev-hi' : pct >= 15 ? 'ev-mid' : pct > 0 ? 'ev-lo' : 'ev-none'; },
    zodiacToggleTip(z) {
      return { Best: 'Opposite sign, opposite gender — ×1.5 damage', Good: 'Compatible sign — ×1.25', Neutral: 'No zodiac modifier', Bad: 'Incompatible sign — ×0.75', Worst: 'Opposite sign, same gender — ×0.5' }[z] || '';
    },

    // --- full-card gear editor ---
    slotLabel(slot) { return { Mainhand: 'Weapon', Offhand: 'Offhand', Head: 'Head', Armor: 'Body', Accessory: 'Accessory' }[slot] || slot; },
    setGear(slot, value) { this.gearOverrides = { ...this.gearOverrides, [slot]: value }; },
    resetGear() { this.gearOverrides = {}; },
    // Legal class weapon types (from classStats), expanded by any Equip-X
    // support the unit has; the whole pool when "show all" is on.
    weaponTypes() {
      if (this.showAllItems) return ALL_WEAPON_TYPES;
      const cs = this.classStatsRec;
      let types = Object.keys(cs.weapons || {});
      const eq = EQUIP_SKILL_TYPE[this.unit.SupportSkill];
      if (eq && cs.equipArms && cs.equipArms[eq]) types = [...new Set([...types, ...Object.keys(cs.equipArms[eq])])];
      return types;
    },
    // Grouped item options for a slot: [{ label: type, items: [names] }].
    slotItems(slot) {
      const cs = this.classStatsRec;
      const all = this.showAllItems;
      const hasEquipShield = this.unit.SupportSkill === 'Equip Shield';
      const group = (types) => types.map((t) => ({ label: t, items: ITEM_BY_TYPE[t] || [] })).filter((g) => g.items.length);
      if (slot === 'Mainhand') {
        const out = group(this.weaponTypes());
        if (hasEquipShield && !all && cs.noWeapons) out.push(...group(['Shield']));
        return out;
      }
      if (slot === 'Head') return group(all ? ['Hat', 'Helmet'] : (cs.heads && cs.heads.length ? cs.heads : ['Hat', 'Helmet']));
      if (slot === 'Armor') return group(all ? ['Clothing', 'Armor', 'Robe'] : (cs.bodies && cs.bodies.length ? cs.bodies : ['Clothing', 'Robe', 'Armor']));
      if (slot === 'Accessory') return group(['Accessory']);
      if (slot === 'Offhand') {
        const out = [];
        if (all || cs.shield || hasEquipShield) out.push(...group(['Shield']));
        if (all || this.unit.SupportSkill === 'Dual Wield') out.push(...group(this.weaponTypes()));
        return out;
      }
      return [];
    },

    // Style for the unit-name line — picks up the user's custom color
    // for this username (case-insensitively) from highlightColors,
    // returns null otherwise so the default styling applies.
    nameStyle(name) {
      const c = this.highlightColors && this.highlightColors.get((name || '').toLowerCase());
      return c ? { color: c, fontWeight: 'bold' } : null;
    },

    // Thin wrappers over the pure formulas in lib/damage.js. Signatures
    // (slot, paBonus) are preserved so abilityFormula's Charge+N calls work
    // unchanged. targetFaith comes from the on-card slider here.
    weaponDamage(slot, paBonus = 0) {
      return calcWeaponDamage({
        item: this.equipped[slot],
        pa: this.stats.pa, ma: this.stats.ma, speed: this.stats.speed,
        brave: this.braveAdj, faith: this.faithAdj,
        doublehand: this.unit.SupportSkill === 'Doublehand',
        targetFaith: this.targetFaith,
        paBonus,
        // Apply the unit's attacker supports so the displayed basic-attack
        // number matches the fight view / sim (was raw — Attack Up / Magic
        // Attack Up / Strengthen silently never showed on the card).
        attackUp: this.unit.SupportSkill === 'Attack Up',
        magicAttackUp: this.unit.SupportSkill === 'Magic Attack Up',
        strengthen: this.elementProfile.strengthen,
      });
    },

    unarmedDamage(paBonus = 0) {
      return calcUnarmedDamage({
        paUnarmed: this.paUnarmed,
        brave: this.braveAdj,
        isMartialArts: this.unit.SupportSkill === 'Martial Arts' || this.unit.Class === 'Monk',
        paBonus,
        attackUp: this.unit.SupportSkill === 'Attack Up',
      });
    },

    // Shared assembly for the both-teams ability/weapon breakdown. Given a
    // per-target value function evalTarget(ctx, dMods) → display string, build
    // the { kind, primary, formula, rider, attackerMods, sides } object — BOTH
    // teams, the natural side first, the other side the hypothetical "if it hit
    // them". Abilities pass an abilityVsTarget closure; weapons pass a
    // computeAbilityDamage(model, …) one — same both-teams rows either way.
    assembleBreakdown({ kind, primary, isDamage, element, formula, formulaNumbers, formulaValue, rider, attackerMods, baseCtx, evalTarget }) {
      const ZFACTOR = { Good: '×5/4', Bad: '×3/4', Best: '×3/2', Worst: '×1/2' };
      const rowFor = (t) => {
        const generic = !t; // no roster defined → a neutral 60-Faith stand-in
        const compat = generic ? 'Neutral' : zodiacCompat(this.unit.Gender, this.unit.Sign, t.Gender, t.Sign);
        const dMods = { zodiac: compat };
        let effect = null;
        if (isDamage && !generic) {
          const prof = this.targetElementProfile(t);
          dMods.absorb = prof.absorb; dMods.half = prof.half; dMods.weak = prof.weak; dMods.cancel = prof.cancel;
          if (element) {
            if (prof.absorb.has(element)) effect = 'absorb';
            else if (prof.cancel.has(element)) effect = 'cancel';
            else if (prof.weak.has(element)) effect = 'weak';
            else if (prof.half.has(element)) effect = 'half';
          }
        }
        const value = evalTarget({ ...baseCtx, targetFaith: generic ? 60 : (t.Faith || 60) }, dMods);
        return {
          name: generic ? 'any target' : t.Name,
          portrait: generic ? '' : this.unitPortrait(t),
          zodiac: compat, zfactor: ZFACTOR[compat] || null, value, effect, generic,
        };
      };
      const order = primary === 'allies'
        ? [['allies', this.ownTeam], ['enemies', this.enemyTeam]]
        : [['enemies', this.enemyTeam], ['allies', this.ownTeam]];
      const sides = [];
      for (const [key, list] of order) {
        let rows = (list || []).filter(Boolean).map(rowFor);
        // No roster on the natural side (Builder / Champions / a team-less modal)
        // → one generic target (60 Faith, neutral) so the base number still shows.
        if (!rows.length && key === primary) rows = [rowFor(null)];
        if (!rows.length) continue;
        sides.push({
          key,
          label: key === 'enemies' ? '⚔ enemies' : '🛡 your team',
          hypothetical: key !== primary,
          kind,
          rows,
        });
      }
      return { kind, primary, formula, formulaNumbers, formulaValue, rider, attackerMods, sides };
    },
    // Per-target breakdown for the spell hover popup: for a damaging ability,
    // each enemy with its zodiac-aware damage; for a heal, each ally with the
    // heal amount. Null when the ability isn't a recognized damage/heal/status.
    // Reuses abilityVsTarget (the fight view's per-target entry) so the numbers
    // match the matrix; assembleBreakdown lays out both teams.
    targetBreakdown(abilityRaw) {
      const ability = abilityRaw.replace('*', '');
      const info = this.abilitiesInfo[ability];
      if (!info) return null;
      const baseCtx = {
        ma: this.stats.ma, pa: this.stats.pa, speed: this.stats.speed,
        brave: this.braveAdj, faith: this.faithAdj,
        wp: this.equipped.Mainhand.wp, maxHp: this.stats.hp,
      };
      // Attacker-side modifiers — the same for every target, so they drive the
      // shared formula section. Applied to the probe too, so probe.steps carries
      // exactly the attacker multipliers that fire for this unit.
      const aMods = {
        strengthen: this.elementProfile.strengthen,
        attackUp: this.unit.SupportSkill === 'Attack Up',
        magicAttackUp: this.unit.SupportSkill === 'Magic Attack Up',
        martialArts: this.unit.SupportSkill === 'Martial Arts' || this.unit.Class === 'Monk',
      };
      const probe = abilityVsTarget(info, { ...baseCtx, targetFaith: 60 }, aMods);
      if (!probe) return null;
      // Classify the ability's kind + its NATURAL side. A drain (AbsorbHP/MP,
      // DamageMP) is aggressive like damage; Heal/HealMP heals allies; a percent
      // spell is a buff (ally) or an aggressive status (enemy). Since both teams
      // are always listed now, this only decides which side shows FIRST — a
      // misclassification just flips the ordering, never hides a side (the old
      // single-side guess sent ~15 ally cleanses/revives to the enemy list).
      const otherVerb = probe.kind === 'other' ? probe.verb : null;
      const isHeal = otherVerb === 'Heal' || otherVerb === 'HealMP';
      const isDrain = !!otherVerb && !isHeal;              // AbsorbHP / AbsorbMP / DamageMP
      const isDamage = probe.kind === 'damage';
      const isPercent = probe.kind === 'percent';
      // A status (percent) ability leads with ALLIES if it ADDS a good status or
      // CANCELS/REMOVES a bad one — so cleanses/revives (Raise, Esuna, Stabilize
      // Time) and buffs default to your team, while dispels (Dispel Magic, Despair
      // — they Cancel GOOD statuses) and Add-bad debuffs stay enemy-first. Same
      // Cancel verb, opposite intent; the STATUS LIST disambiguates. Both teams
      // still show — this only picks which side leads. (Defending/Performing are
      // left out of "good": aggressive adds like Noxious Gas / Gil Taking include
      // them incidentally and must stay enemy-first.)
      const GOOD_STATUS = 'Protect|Shell|Haste|Regen|Reraise|Float|Reflect|Wall|Transparent|Faith';
      const BAD_STATUS = 'Death|Petrify|Darkness|Blind|Confusion|Silence|Poison|Sleep|Frog|Chicken|Stop|Charm|Berserk|Oil|Undead|Slow|Innocent|Blood';
      const isBeneficial = isPercent && (
        new RegExp(`\\bAdd\\b[^.;]*\\b(?:${GOOD_STATUS})\\b`, 'i').test(info) ||
        new RegExp(`\\b(?:Cancel|Remove)\\b[^.;]*\\b(?:${BAD_STATUS})\\b`, 'i').test(info)
      );
      const kind = isHeal ? 'heal' : isBeneficial ? 'buff' : (isDamage || isDrain) ? 'damage' : 'status';
      const primary = (kind === 'heal' || kind === 'buff') ? 'allies' : 'enemies';
      // Only TRUE damage gets target-affinity effect coloring — the engine only
      // applies weak/half/absorb on the Damage verb (drains stay base).
      const element = isDamage ? probe.element : null;

      // Shared formula section: the base formula text (from the API info) + the
      // attacker multipliers that actually apply to THIS unit (the Damage-verb
      // step chain after "Base").
      const formula = formulaText(info);
      const formulaNumbers = substituteFormula(formula, { ...baseCtx, targetFaith: 60 });
      // The base result (no attacker/target mods, generic 60 Faith) — the "= N"
      // that closes out the actual-values line.
      const baseRes = abilityVsTarget(info, { ...baseCtx, targetFaith: 60 }, {});
      const formulaValue = baseRes ? baseRes.display : null;
      const rider = riderStatuses(info); // "…Chance to add <status>" on a damage hit
      const attackerMods = isDamage
        ? (probe.steps || []).slice(1).map((s) => ({
            label: s.label === 'Strengthen' && element ? `Strengthen ${element}` : s.label,
            factor: s.factor,
          }))
        : [];

      return this.assembleBreakdown({
        kind, primary, isDamage, element, formula, formulaNumbers, formulaValue, rider, attackerMods, baseCtx,
        evalTarget: (ctx, dMods) => {
          const r = abilityVsTarget(info, ctx, { ...aMods, ...dMods });
          return r ? r.display : '—';
        },
      });
    },
    // Basic-attack breakdown for a weapon slot — the weapon's attack model run
    // through the SAME both-teams pipeline as an ability (computeAbilityDamage
    // per target). Mainhand with no weapon / disarmed falls back to unarmed; an
    // empty Offhand yields nothing. Monsters attack with their kit, not gear.
    buildWeaponBreakdown(slot) {
      if (this.unit.Gender === 'Monster') return null;
      const forceUnarmed = slot === 'unarmed'; // the standalone bare-hands row
      const item = forceUnarmed ? null : this.equipped[slot];
      let model = item && item.type ? weaponAttackModel(item, this.unit) : null;
      let formula, rider, wp, isUnarmed = false;
      if (model) {
        formula = weaponFormulaText(item, this.unit);
        rider = riderStatuses(item.info);
        wp = item.wp;
      } else if (forceUnarmed || slot === 'Mainhand') {
        model = unarmedAttackModel(this.unit); // no weapon / disarmed → bare hands
        formula = unarmedFormulaText(this.unit);
        rider = null; wp = 0; isUnarmed = true;
      } else {
        return null;
      }
      const baseCtx = {
        // Bare hands scale off paUnarmed (class base + non-weapon PA), so the
        // numeric line substitutes that for PA rather than the weapon-inclusive stat.
        ma: this.stats.ma, pa: isUnarmed ? this.paUnarmed : this.stats.pa, speed: this.stats.speed,
        brave: this.braveAdj, faith: this.faithAdj,
        wp, maxHp: this.stats.hp, paUnarmed: this.paUnarmed,
      };
      const aMods = {
        strengthen: this.elementProfile.strengthen,
        attackUp: this.unit.SupportSkill === 'Attack Up',
        magicAttackUp: this.unit.SupportSkill === 'Magic Attack Up',
        martialArts: this.unit.SupportSkill === 'Martial Arts' || this.unit.Class === 'Monk',
      };
      const element = model.element || null;
      const attackerMods = (computeAbilityDamage(model, { ...baseCtx, targetFaith: 60 }, aMods).steps || [])
        .slice(1).map((s) => ({ label: s.label === 'Strengthen' && element ? `Strengthen ${element}` : s.label, factor: s.factor }));
      const formulaNumbers = substituteFormula(formula, { ...baseCtx, targetFaith: 60 });
      const fmt = (f) => (Array.isArray(f) ? `${f[0]} - ${f[1]}` : `${f}`);
      const formulaValue = fmt(computeAbilityDamage(model, { ...baseCtx, targetFaith: 60 }, {}).final);
      return this.assembleBreakdown({
        kind: 'damage', primary: 'enemies', isDamage: true, element,
        formula, formulaNumbers, formulaValue, rider, attackerMods, baseCtx,
        evalTarget: (ctx, dMods) => fmt(computeAbilityDamage(model, ctx, { ...aMods, ...dMods }).final),
      });
    },
    // Elemental affinity (absorb/half/weak/strengthen/cancel sets) for an
    // arbitrary opposing unit, from its equipped gear. Monsters have no gear →
    // empty sets. Used to make the per-target spell breakdown element-aware.
    targetElementProfile(u) {
      const p = unitElementProfile({
        Mainhand:  this.itemschanged[u.Mainhand],
        Offhand:   this.itemschanged[u.Offhand],
        Head:      this.itemschanged[u.Head],
        Armor:     this.itemschanged[u.Armor],
        Accessory: this.itemschanged[u.Accessory],
      });
      // A monster target has no gear but does have innate affinities (Hydra is
      // weak to Ice/Wind) — merge them in, from the live tips (see monsterElements).
      if (u.Gender === 'Monster') {
        const key = (u.Class || '').replace(/\s+/g, '');
        const mel = parseItemElements(this.toolTips.Class && this.toolTips.Class[key]);
        for (const k of Object.keys(p)) for (const e of mel[k]) p[k].add(e);
      }
      return p;
    },
    // Portrait gif for an arbitrary unit (team-less; uses palette-less sprite).
    unitPortrait(u) {
      if (!u || !u.Class) return '';
      if (u.Gender === 'Monster') return this.IMG_URL + u.Class + '.gif';
      return this.IMG_URL + u.Class + (u.Gender ? u.Gender[0] : 'M') + '.gif';
    },
    abilityFormula(ability) {
      ability = ability.replace("*", "")

      // Charge+N: same weapon-by-type damage as a basic attack, but with
      // +N PA. Collapses what were eight near-identical 50-line switch cases.
      const charge = ability.match(/^Charge\+(\d+)$/);
      if (charge) {
        const n = +charge[1];
        return this.weaponDamage('Mainhand', n) ?? this.unarmedDamage(n);
      }

      // Parser-first: derive the formula from the abilities API `info`
      // (lib/abilityFormula.js). Returns '' for abilities it doesn't
      // recognize, in which case we fall through to the legacy switch below.
      const info = this.abilitiesInfo[ability];
      if (info) {
        const parsed = abilityDisplay(info, {
          ma: this.stats.ma, pa: this.stats.pa, speed: this.stats.speed,
          brave: this.braveAdj, faith: this.faithAdj,
          targetFaith: this.targetFaith,
          wp: this.equipped.Mainhand.wp, maxHp: this.stats.hp,
        }, {
          // Attacker-only modifiers (correct regardless of target): the unit's
          // elemental Strengthen, plus Attack UP / Magic Attack UP supports.
          strengthen: this.elementProfile.strengthen,
          attackUp: this.unit.SupportSkill === 'Attack Up',
          magicAttackUp: this.unit.SupportSkill === 'Magic Attack Up',
          // Martial Arts (support, or Monk innate) → ×3/2 on Punch Art skills.
          martialArts: this.unit.SupportSkill === 'Martial Arts' || this.unit.Class === 'Monk',
          // Hypothetical target zodiac compatibility (the tuning toggle).
          zodiac: this.zodiacAdj,
        });
        if (parsed) return parsed;
      }

      // Legacy fallback for forms the parser can't express yet — Jump, the
      // punch-art break skills, monster weapon skills, songs (lib/abilityFormula.js).
      // Reads `equipped`, so "treat weapon as removed" affects these too.
      const legacy = legacyAbilityFormula(ability, {
        pa: this.stats.pa, ma: this.stats.ma,
        brave: this.braveAdj, faith: this.faithAdj, targetFaith: this.targetFaith,
        wp: this.equipped.Mainhand.wp, weaponType: this.equipped.Mainhand.type,
        paUnarmed: this.paUnarmed,
      });
      return legacy ? legacy.display : '';
    },
  },

  data() {
    return {
      // Compact by default (short, screenshot-friendly); click to expand —
      // unless the "expand cards by default" setting seeds it open.
      expanded: this.defaultExpanded,
      // Damage-tuning panel (sliders + disarm), collapsed within the detail view.
      showTuning: false,
      // Treat the weapon as removed — reveals/uses the unarmed attack.
      disarmed: false,
      // Full-card gear editor: per-slot overrides ({} = use the rolled gear),
      // and the edit/sandbox toggles. Local + discarded when the modal closes.
      gearOverrides: {},
      editGear: false,
      showAllItems: false,
      // Local Brave/Faith used by the damage math, seeded from the unit so the
      // sliders let you see how B/F swings spell/ability numbers. (Avoid the
      // literal `unit.Brave/Faith` form here — it's threaded into the formulas.)
      braveAdj: (this.unit || {}).Brave || 70,
      faithAdj: (this.unit || {}).Faith || 70,
      // Local target-faith slider state. Initialized from the prop (if the
      // upstream API ever provides one) with a 60 fallback.
      targetFaith: (this.unit && this.unit.targetFaith) || 60,
      // Hypothetical zodiac compatibility vs the target, for the damage preview.
      // 'Neutral' by default; the tuning toggles set Best/Good/Bad/Worst.
      zodiacAdj: 'Neutral',
      IMG_URL: "https://luxor-fftbg-assets.s3.amazonaws.com/units/",
      IMG_URL_ITEMS: "https://luxor-fftbg-assets.s3.amazonaws.com/icons/",
    };
  },
  watch: {
    // Flip already-mounted cards when the "expand by default" setting changes.
    defaultExpanded(v) { this.expanded = v; },
    // Re-seed the tuning sliders when the rendered unit changes (live poll
    // roster updates, or the generator re-rolling into the same component).
    unit() {
      this.braveAdj = (this.unit || {}).Brave || 70;
      this.faithAdj = (this.unit || {}).Faith || 70;
      this.disarmed = false;
      this.zodiacAdj = 'Neutral';
      this.gearOverrides = {};
      this.editGear = false;
    },
  },
}
</script>